<template>
    <div class="h-screen flex flex-col">
            <div v-if="preview" class="w-full py-2 text-sm bg-gold text-center">
                <span class="uppercase font-medium">Preview</span> - Responses will not be saved
            </div>
            <div class="border-b border-gray-400 bg-white flex flex-col md:flex-row flex-wrap justify-center items-center p-4 w-full gap-y-1">
                <h1 class="survey-title flex-1 flex items-center gap-x-2 text-base md:text-xl"> <NuxtImg class="h-4 md:h-5" v-if="respData?.logoUrl" :src="respData?.logoUrl" /> {{ respData?.data?.name }}</h1>
                <div v-show="!surveyCompleted" class="progress-indicator"  :class="{'text-gray-700': theme === 'plain', 'text-black': theme !== 'plain'}">{{percentComplete}}
                    % complete
                    <div class="w-28 h-1 flex items-center justify-start bg-gray-200">
                        <div class="h-1" :class="{'bg-gray-700': theme === 'plain', 'bg-orange': theme !== 'plain'}" :style="`width: ${percentComplete}%`"></div>
                        <div class="h-1 w-1 bg-white"></div>
                    </div>
                </div>
                <div class="flex-1 flex justify-end min-w-fit">
                    <span v-if="userData?.data?.name" class="user-indicator text-sm md:text-base">Responding as <span class="font-semibold">&nbsp;{{ userData.data.name }}&nbsp;</span></span>
                </div>
            </div>
<!--        <div v-if="currentStep === Steps.Landing" class="flex flex-col items-center gap-6 pt-12">-->
<!--            <h1 v-if="loggedInAs" class="text-black text-xl font-bold max-w-sm">Hi {{ loggedInAs }},</h1>-->
<!--            <p>-->
<!--                You've been asked to fill out the following survey:  <strong>{{ surveyName }}</strong>-->
<!--            </p>-->
<!--            <Button type="primary" @click.prevent="setTab(Steps.Form)">Take Survey</Button>-->
<!--        </div>-->
        <ClientOnly v-if="survey && !respData?.data?.surveyClosed">
            <SurveyComponent id="survey-component" :model="survey" :userId="userData?.data?.id" />
        </ClientOnly>
        <div v-else class="justify-items-center mt-10">
            <p  class="text-xl">This survey is currently closed.</p>
        </div>
    </div>
</template>

<style scoped>
    .survey-title{
        font-size: theme('fontSize.h2');
        padding: theme('spacing.1');
    }

    .progress-indicator{
        align-items: center;
        column-gap: theme('spacing.1');
        display: flex;
        flex-direction: column;
        font-size: theme('fontSize.small');
        justify-content: flex-end;
        justify-items: center;
        padding: theme('spacing.1');
        text-transform: uppercase;
    }

    .user-indicator{
        align-self: flex-start;
        display: flex;
        padding: theme('spacing.1');
        color: theme('colors.gray.700')
    }

    @media (min-width: 600px) {
        #survey-component:deep(.sd-page__title)::before {
            content: url("/brand-chevrons.svg");
            position: absolute;
            display: v-bind(chevronCSS);
            left: 0;
        }
    }

    #survey-component:deep(.sd-completedpage) {
        padding: 0 36px;
    }
</style>

<script setup lang="ts">
definePageMeta({
    skipMiddleware: true, // Custom property to signal skipping middleware
    layout: false
});

import { SurveyComponent } from "survey-vue3-ui";
import {SURVEY_THEME, SURVEY_THEME_DARK, THEME_TO_BACKGROUND} from "~/constants";
import { Model } from "survey-core";
import { buildChoiceLabelSnapshot, CHOICE_LABELS_KEY } from "~/utils/surveyChoices";

const route = useRoute()
const router = useRouter()
const { slug } = route.params
const preview = route.query.preview
const surveyComplete = ref(false);

const survey = ref<Model | null>(null)
const percentComplete = ref(0)
const surveyCompleted = ref(false)
const { $secureFetch } = useNuxtApp();

const { data: respData } = await $secureFetch(`/api/${route.params.org_slug}/forms/${slug}/token`); 

const { data: userData } = await $secureFetch(`/api/${route.params.org_slug}/respondent/${route.query.token}`)

const theme = computed(() => respData.value?.data?.theme || 'plain')
const chevronCSS = computed(() => (theme.value !== 'plain') ? 'block' : 'none')

if (respData.value?.data?.data) {
    survey.value = new Model(JSON.parse(respData.value.data.data));
    survey.value.onProgressText.add((sender, options) => {
      percentComplete.value = Math.trunc(
          100 * (options.answeredQuestionCount / options.questionCount)
      )
    });

    survey.value.applyTheme({
      ...(respData.value.data.theme === 'dark' ? SURVEY_THEME_DARK : SURVEY_THEME),
      backgroundImage: THEME_TO_BACKGROUND[respData.value.data.theme || 'plain']
    })

    const createResponse = async () => {
        surveyCompleted.value = true
        if(!preview) {
            try {
                // Store the raw SurveyJS values (not the display text) so the Analysis
                // tab (survey-analytics) can aggregate them — it matches answers against
                // each choice's `value`, so storing text would drop any response whose
                // choice value differs from its label. Alongside the values, stash a
                // value -> text snapshot so a choice that is later removed/renamed can
                // still be shown with the label the respondent saw (see
                // reconcileRemovedChoices in reporting).
                // Guard: never let the snapshot key overwrite a submitted answer in the
                // unlikely case a question is named CHOICE_LABELS_KEY — keep the answer
                // and skip the snapshot (reporting falls back to the raw value as label).
                const answers = survey.value?.data ?? {}
                const responseData = CHOICE_LABELS_KEY in answers
                    ? answers
                    : { ...answers, [CHOICE_LABELS_KEY]: buildChoiceLabelSnapshot(survey.value) }

                await $secureFetch(`/api/${route.params.org_slug}/${slug}/responses`, {
                    method: 'POST',
                    body: {
                        email: userData.value?.email,
                        user_id: userData.value?.data?.id,
                        data: JSON.stringify(responseData),
                        status: 'submitted'
                    }
                })
            } catch (error) {
                console.error('Failed to create response:', error)
            }
        }
        surveyComplete.value = true;
    }

    survey.value.onComplete.add(createResponse)
}
</script>
